import { useRef, useState, useEffect } from 'react';
import { Form, FormApi, FormState, FieldState } from 'informed';
import { TyInput, TyDatePicker, TySelectInput, TyOption } from '@tymbe/ui-forms';
import TyMultiSelect from '../../components/inputs/TyMultiSelect';
import { PrimaryButton, SecondaryButton } from '@tymbe/ui-primitives/*';
import Spinner from '../../components/Spinner';
import { FavouriteIcon } from '@tymbe/ui-icons';
import { informedConfig } from '../../config/informed';
import {
  AdminPopupFormValues,
  getSortedCompanyOptions,
  validateFromBeforeTo,
  getFilteredWeekdayOptions,
  adminPopupFormDefaultValues,
  adminPopupModuleOptions,
} from './adminPopupUtils';
import moment from 'moment';
import TyMarkdownEditor from '../../components/inputs/TyMarkdownEditor';
import { CompanyData } from '@tymbe/schema/company.interface';
import { AdminPopupDisplayFrequencyDaysEnum, AdminPopupImageEnum } from '@tymbe/schema/admin-popup.interface';

import AdminPopupImageSelector from './AdminPopupImageSelector';

interface AdminPopupFormProps {
  mode: 'create' | 'edit';
  initialValues?: AdminPopupFormValues;
  onSubmit: (data: FormState<AdminPopupFormValues>) => void;
  onCancel?: () => void;
  companies: CompanyData[];
  isLoading: boolean;
  mutationLoading: boolean;
  title: string;
}

export default function AdminPopupForm({
  mode,
  initialValues,
  onSubmit,
  onCancel,
  companies,
  isLoading,
  mutationLoading,
  title,
}: AdminPopupFormProps) {
  const formApiRef = useRef<FormApi<AdminPopupFormValues> | undefined>(undefined);

  // Merge initialValues with defaults to ensure all required fields are present
  const mergedInitialValues: AdminPopupFormValues = {
    ...adminPopupFormDefaultValues,
    ...initialValues,
  };

  const [displayFrequency, setDisplayFrequency] = useState<AdminPopupDisplayFrequencyDaysEnum | null>(
    mergedInitialValues.display_frequency
  );
  const [fromDate, setFromDate] = useState<moment.Moment | null>(mergedInitialValues.from);
  const [toDate, setToDate] = useState<moment.Moment | null>(mergedInitialValues.to);
  const [selectedImage, setSelectedImage] = useState<AdminPopupImageEnum>(mergedInitialValues.image);
  const [buttonText, setButtonText] = useState<string | null>(mergedInitialValues.button_text);

  // For create mode, filter weekday options based on date range
  const filteredWeekdayOptions = getFilteredWeekdayOptions(fromDate, toDate);

  useEffect(() => {
    if (mode === 'edit' && initialValues?.display_frequency) {
      setDisplayFrequency(initialValues.display_frequency);
    }
  }, [initialValues?.display_frequency, mode]);

  if (isLoading) {
    return <div className="p-5">Načítání...</div>;
  }

  return (
    <div className="h-full w-full">
      <Spinner show={isLoading} absolute className="z-20 h-8 w-8 m-auto" />
      <FavouriteIcon
        name="close"
        className="h-6 w-6 absolute right-8 cursor-pointer ml-auto my-auto"
        onClick={onCancel}
      />
      <Form
        onSubmit={onSubmit}
        initialValues={mergedInitialValues}
        formApiRef={formApiRef}
        className="flex flex-col max-h-full"
      >
        <h1 className="text-heading-3-md font-semibold text-secondary-600">{title}</h1>
        <div className="flex-shrink overflow-auto flex flex-col gap-2 pr-1 my-5">
          <TyInput
            name="name"
            label="Název (viditelný pouze v tabulce administrace)"
            required={informedConfig.errorMessage.required}
          />
          <div className="flex flex-row gap-4">
            <TyDatePicker
              name="from"
              label="Aktivní od"
              validateWhen={['to']}
              picker="date"
              validateOn='change'
              validate={(value) => validateFromBeforeTo(value, formApiRef)}
              onChange={(value: FieldState<moment.Moment | undefined>) => setFromDate(value.value || null)}
              isClearable
              clearIcon={<FavouriteIcon name="close" className="h-4 w-4" />}
              className="w-1/2"
            />
            <TyDatePicker
              name="to"
              label="Aktivní do"
              picker="date"
              validateOn='change'
              isClearable
              className="w-1/2"
              clearIcon={<FavouriteIcon name="close" className="h-4 w-4" />}
              onChange={(value: FieldState<moment.Moment>) => setToDate(value.value)}
            />
          </div>
          <AdminPopupImageSelector
            selectedImage={selectedImage}
            setSelectedImage={setSelectedImage}
            setFormValue={(img) => formApiRef.current?.setValue('image', img)}
          />
          <div>
            <TyMarkdownEditor
              label="Obsah"
              name="content"
              initialValue={formApiRef.current?.getValue('content')}
              required={informedConfig.errorMessage.required}
            />
          </div>
          <TyInput
            name="button_text"
            label="Text tlačítka"
            onChange={(value) => {
              if (!value.value) {
                formApiRef.current?.setValue('button_url', null);
              }
              setButtonText(value.value as string);
            }}
          />
          <TyInput
            disabled={!buttonText}
            name="button_url"
            label="URL tlačítka"
            required={buttonText ? informedConfig.errorMessage.required : undefined}
          />
          <TySelectInput
            name="display_frequency"
            label="Frekvence zobrazení"
            required={informedConfig.errorMessage.required}
            onChange={(value: FieldState<AdminPopupDisplayFrequencyDaysEnum>) => {
              if (value.value !== AdminPopupDisplayFrequencyDaysEnum.weekly) {
                formApiRef.current?.setValue('display_frequency_weekday', null);
              }
              setDisplayFrequency(value.value);
            }}
          >
            <TyOption value={AdminPopupDisplayFrequencyDaysEnum.once} label="Pouze jednou">
              Pouze jednou
            </TyOption>
            <TyOption value={AdminPopupDisplayFrequencyDaysEnum.daily} label="Denně">
              Denně
            </TyOption>
            <TyOption value={AdminPopupDisplayFrequencyDaysEnum.weekly} label="Týdně">
              Týdně
            </TyOption>
          </TySelectInput>
          <TyMultiSelect
            name="display_frequency_weekday"
            label="Pokud týdně, pak které dny v týdnu?"
            options={filteredWeekdayOptions}
            isDisabled={displayFrequency !== AdminPopupDisplayFrequencyDaysEnum.weekly}
            required={
              displayFrequency === AdminPopupDisplayFrequencyDaysEnum.weekly && informedConfig.errorMessage.required
            }
          />
          <TyMultiSelect
            menuPlacement="top"
            name="company_ids"
            label="Pro které firmy se bude zobrazovat? Pokud je vynecháno, pak se defaultně nastavuje pro všechny firmy"
            options={getSortedCompanyOptions(companies)}
          />
          <TyMultiSelect
            menuPlacement="top"
            name="show_on_modul_page"
            label="Na jakých stránkách bude zobrazovat? Pokud je vynecháno, pak se defaultně nastavuje stránka kalendáře"
            options={adminPopupModuleOptions}
          />
        </div>
        <div className="flex gap-2 bg-white">
          <PrimaryButton type="submit" color="primary" className="w-fit" disabled={mutationLoading}>
            {mode === 'edit' ? 'Uložit změny' : 'Vytvořit popup'}
          </PrimaryButton>
          {onCancel && (
            <SecondaryButton type="button" className="text-body-2-xl ml-2" onClick={onCancel}>
              Zrušit
            </SecondaryButton>
          )}
        </div>
      </Form>
    </div>
  );
}
